import { useState } from 'react';
import { RICERCA_ARTICOLI, useArticoli } from '../api/articoli.js';
import { ErroreApi } from '../api/cliente.js';
import {
  type OpzioniListino,
  type RigaPrezzo,
  useAggiungiDestinazione,
  useAggiungiRigaListino,
  useIndirizziListino,
  useSalvaRigaListino,
  useCorreggiDestinazione,
  useTogliDestinazione,
} from '../api/listini.js';
import { Modale } from '../componenti/Modale.js';
import { VoceArticolo } from '../componenti/VoceArticolo.js';
import { importo } from '../formati.js';
import { SKU_ASSENTE } from './Articoli.js';
import { t } from '../i18n/lingua.js';

/**
 * Aggiungere un articolo al listino, o cambiarne tutti i valori.
 *
 * Sulla riga si corregge il prezzo, che è la modifica di ogni giorno. Qui si
 * fa il resto — costo DDP, aliquota, i tre sconti, il riferimento del cliente
 * — che si tocca di rado e merita una finestra.
 *
 * L'articolo si sceglie cercandolo, non da una tendina: sono più di
 * ottocento, e una tendina di ottocento voci è un elenco in cui non si trova
 * niente.
 */
export function ModaleRigaListino({
  listinoId,
  riga,
  opzioni,
  giaPresenti,
  onChiudi,
}: {
  listinoId: number;
  /** Nullo per una riga nuova. */
  riga: RigaPrezzo | null;
  opzioni: OpzioniListino | undefined;
  /** Gli articoli già a listino: non si propongono due volte. */
  giaPresenti: number[];
  onChiudi: () => void;
}) {
  const [cerca, setCerca] = useState('');
  const [articoloId, setArticoloId] = useState<number | null>(riga?.articleId ?? null);
  const [campi, setCampi] = useState({
    priceExw: riga?.priceExw ?? '',
    costDdp: riga?.costDdp ?? '0',
    taxId: riga ? String(riga.taxId) : '',
    crossReference: riga?.crossReference ?? '',
    discountPercentage1: riga?.discountPercentage1 ?? '',
    discountPercentage2: riga?.discountPercentage2 ?? '',
    discountPercentage3: riga?.discountPercentage3 ?? '',
  });

  const aggiungi = useAggiungiRigaListino(listinoId);
  const salva = useSalvaRigaListino(listinoId);

  // la ricerca parte solo quando si è scritto qualcosa: aprire la finestra
  // non deve caricare ottocento articoli che nessuno ha chiesto. Prima la
  // richiesta partiva lo stesso, e si nascondeva solo il risultato
  const abbastanza = cerca.trim().length >= 2;
  const trovati = useArticoli({ ...RICERCA_ARTICOLI, q: cerca, size: 20 }, abbastanza);
  const elenco = abbastanza ? (trovati.data?.rows ?? []) : [];

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const inCorso = aggiungi.isPending || salva.isPending;
  const errore =
    aggiungi.error instanceof ErroreApi
      ? aggiungi.error.message
      : salva.error instanceof ErroreApi
        ? salva.error.message
        : null;

  const numeroOpzionale = (valore: string) => (valore === '' ? null : Number(valore));

  const conferma = () => {
    const corpo = {
      priceExw: Number(campi.priceExw),
      costDdp: Number(campi.costDdp || 0),
      ...(campi.taxId ? { taxId: Number(campi.taxId) } : {}),
      crossReference: campi.crossReference.trim() || null,
      discountPercentage1: numeroOpzionale(campi.discountPercentage1),
      discountPercentage2: numeroOpzionale(campi.discountPercentage2),
      discountPercentage3: numeroOpzionale(campi.discountPercentage3),
    };

    if (riga) salva.mutate({ lineId: riga.id, ...corpo }, { onSuccess: onChiudi });
    else if (articoloId !== null)
      aggiungi.mutate({ articleId: articoloId, ...corpo }, { onSuccess: onChiudi });
  };

  const scelto = elenco.find((a) => a.id === articoloId);

  return (
    <Modale
      titolo={riga ? "Modifica l'articolo del listino" : t('Aggiungi un articolo al listino')}
      larga
      inCorso={inCorso}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={inCorso}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={inCorso || (!riga && articoloId === null) || String(campi.priceExw).trim() === ''}
          >
            {inCorso ? 'Salvo…' : 'Salva'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      {riga ? (
        <div className="fields" style={{ marginBottom: 14 }}>
          <div className="field span-2">
            <label>{t('Articolo')}</label>
            <div className="readonly">
              {riga.description || riga.sku}
              {riga.sku && riga.sku !== SKU_ASSENTE && (
                <span className="dim num" style={{ marginLeft: 8 }}>
                  {riga.sku}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="field span-2" style={{ marginBottom: 14 }}>
          <label htmlFor="rl-cerca">
            {t('Articolo')}<span className="req">*</span>
            <span className="dim" style={{ fontWeight: 400 }}>
              {' '}
              {t('— cerca per descrizione, SKU o numero BC')}
            </span>
          </label>
          <input
            id="rl-cerca"
            className="input"
            placeholder={t('Scrivi almeno due lettere')}
            value={cerca}
            autoFocus
            onChange={(e) => {
              setCerca(e.target.value);
              setArticoloId(null);
            }}
          />
          {scelto ? (
            <div className="scelto">
              <VoceArticolo articolo={scelto} />
              <button className="btn btn-ghost" onClick={() => setArticoloId(null)}>
                {t('Cambia')}
              </button>
            </div>
          ) : (
            elenco.length > 0 && (
              <ul className="risultati">
                {elenco.map((a) => {
                  const presente = giaPresenti.includes(a.id);
                  return (
                    <li key={a.id}>
                      <button
                        className="risultato"
                        disabled={presente}
                        title={presente ? t('Già in questo listino') : undefined}
                        onClick={() => setArticoloId(a.id)}
                      >
                        <VoceArticolo articolo={a} />
                        {presente && <span className="badge">{t('già a listino')}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>
      )}

      <div className="fields">
        <div className="field">
          <label htmlFor="rl-prezzo">
            {t('Prezzo EXW')}<span className="req">*</span>
          </label>
          <input
            id="rl-prezzo"
            className="input num"
            type="number"
            step="0.001"
            min="0"
            value={campi.priceExw}
            onChange={(e) => cambia('priceExw', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="rl-ddp">{t('Costo DDP')}</label>
          <input
            id="rl-ddp"
            className="input num"
            type="number"
            step="0.001"
            min="0"
            value={campi.costDdp}
            onChange={(e) => cambia('costDdp', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="rl-iva">{t('Aliquota')}</label>
          <select
            id="rl-iva"
            className="select"
            value={campi.taxId}
            onChange={(e) => cambia('taxId', e.target.value)}
          >
            <option value="">{t('Quella predefinita')}</option>
            {opzioni?.taxes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
                {t.isDefault ? ' (predefinita)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field span-2">
          <label htmlFor="rl-rif">
            {t('Riferimento del cliente')}
            <span className="dim" style={{ fontWeight: 400 }}> {t('— il codice con cui lo chiama lui')}</span>
          </label>
          <input
            id="rl-rif"
            className="input"
            value={campi.crossReference}
            onChange={(e) => cambia('crossReference', e.target.value)}
          />
        </div>
        {/* tre sconti in cascata, come nel v2: si applicano uno dopo l'altro */}
        {([1, 2, 3] as const).map((n) => (
          <div className="field" key={n}>
            <label htmlFor={`rl-sc${n}`}>{t('Sconto {n} (%)', { n })}</label>
            <input
              id={`rl-sc${n}`}
              className="input num"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={campi[`discountPercentage${n}` as keyof typeof campi]}
              onChange={(e) => cambia(`discountPercentage${n}` as keyof typeof campi, e.target.value)}
            />
          </div>
        ))}
      </div>

      {/**
       * I prezzi per destinazione stanno qui dentro, come nel v2: sono
       * eccezioni a questa riga, e vederli altrove — lontano dal prezzo che
       * sostituiscono — non direbbe niente.
       *
       * Compaiono solo su una riga che esiste già: un'eccezione a un prezzo
       * non ancora salvato non avrebbe a cosa attaccarsi.
       */}
      {riga && <PrezziPerDestinazione listinoId={listinoId} riga={riga} opzioni={opzioni} />}
    </Modale>
  );
}


/**
 * I prezzi che valgono solo per una destinazione.
 *
 * Il prezzo di riga vale per tutti; questo lo sostituisce per un indirizzo
 * solo. Nel v2 è una griglia dentro la stessa finestra, ed è giusto: si
 * guarda mentre si guarda il prezzo che sta correggendo.
 */
function PrezziPerDestinazione({
  listinoId,
  riga,
  opzioni,
}: {
  listinoId: number;
  riga: RigaPrezzo;
  opzioni: OpzioniListino | undefined;
}) {
  const [aperto, setAperto] = useState(false);
  const [addressId, setAddressId] = useState('');
  const [priceExw, setPriceExw] = useState('');
  const [taxId, setTaxId] = useState('');

  const indirizzi = useIndirizziListino(listinoId, aperto);
  const aggiungi = useAggiungiDestinazione(listinoId);
  const togli = useTogliDestinazione(listinoId);
  const correggi = useCorreggiDestinazione(listinoId);
  /** La destinazione che si sta correggendo, e i suoi valori scritti adesso. */
  const [inModifica, setInModifica] = useState<{
    id: number;
    priceExw: string;
    costDdp: string;
    sconto: string;
  } | null>(null);
  const erroreCorrezione = correggi.error instanceof ErroreApi ? correggi.error.message : null;

  const salvaCorrezione = () => {
    if (!inModifica) return;
    correggi.mutate(
      {
        lineId: riga.id,
        destId: inModifica.id,
        priceExw: Number(inModifica.priceExw),
        costDdp: Number(inModifica.costDdp || 0),
        discountPercentage1: inModifica.sconto === '' ? null : Number(inModifica.sconto),
      },
      { onSuccess: () => setInModifica(null) },
    );
  };

  const gia = riga.destinations.map((d) => d.addressId);
  const disponibili = (indirizzi.data?.addresses ?? []).filter((a) => !gia.includes(a.id));
  const errore = aggiungi.error instanceof ErroreApi ? aggiungi.error.message : null;

  const conferma = () =>
    aggiungi.mutate(
      {
        lineId: riga.id,
        addressId: Number(addressId),
        priceExw: Number(priceExw),
        ...(taxId ? { taxId: Number(taxId) } : {}),
      },
      {
        onSuccess: () => {
          setAddressId('');
          setPriceExw('');
          setAperto(false);
        },
      },
    );

  return (
    <div className="destinazioni">
      <div className="destinazioni-testa">
        <strong>{t('Prezzi per destinazione')}</strong>
        <span className="dim" style={{ fontSize: 11 }}>
          {t('sostituiscono il prezzo qui sopra, per quell’indirizzo')}
        </span>
        {!aperto && (
          <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setAperto(true)}>
            {t('+ Aggiungi')}
          </button>
        )}
      </div>

      {riga.destinations.length === 0 && !aperto && (
        <p className="dim" style={{ margin: '6px 0 0', fontSize: 12 }}>
          {t('Nessuno: vale il prezzo di riga per tutte le destinazioni.')}
        </p>
      )}

      {riga.destinations.length > 0 && (
        <ul className="doclist">
          {riga.destinations.map((d) =>
            inModifica?.id === d.id ? (
              <li key={d.id} style={{ flexWrap: 'wrap' }}>
                <span>{d.address ?? t('Destinazione {n}', { n: d.addressId })}</span>
                <label className="dim" style={{ fontSize: 11, marginLeft: 'auto' }}>
                  {t('Prezzo EXW')}
                  <input
                    className="input num"
                    style={{ width: 100, marginLeft: 6 }}
                    type="number"
                    step="0.001"
                    min="0"
                    autoFocus
                    value={inModifica.priceExw}
                    onChange={(e) => setInModifica({ ...inModifica, priceExw: e.target.value })}
                  />
                </label>
                <label className="dim" style={{ fontSize: 11 }}>
                  {t('Costo DDP')}
                  <input
                    className="input num"
                    style={{ width: 90, marginLeft: 6 }}
                    type="number"
                    step="0.001"
                    min="0"
                    value={inModifica.costDdp}
                    onChange={(e) => setInModifica({ ...inModifica, costDdp: e.target.value })}
                  />
                </label>
                <label className="dim" style={{ fontSize: 11 }}>
                  {t('Sconto %')}
                  <input
                    className="input num"
                    style={{ width: 70, marginLeft: 6 }}
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={inModifica.sconto}
                    onChange={(e) => setInModifica({ ...inModifica, sconto: e.target.value })}
                  />
                </label>
                <button className="btn btn-ghost" onClick={() => setInModifica(null)}>
                  {t('Annulla')}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={correggi.isPending || inModifica.priceExw.trim() === ''}
                  onClick={salvaCorrezione}
                >
                  {correggi.isPending ? t('Salvo…') : t('Salva')}
                </button>
                {erroreCorrezione && (
                  <span style={{ color: 'var(--tomato)', fontSize: 12, width: '100%' }}>
                    {erroreCorrezione}
                  </span>
                )}
              </li>
            ) : (
              <li key={d.id}>
                <span>{d.address ?? t('Destinazione {n}', { n: d.addressId })}</span>
                <span className="dim">{[d.city, d.country].filter(Boolean).join(', ')}</span>
                <strong className="num" style={{ marginLeft: 'auto' }}>
                  {importo(d.priceExw)}
                </strong>
                {/* si corregge sul posto, come nel v2: togliere e rimettere
                    voleva dire riscrivere anche sconti e date */}
                <button
                  className="btn btn-ghost"
                  onClick={() =>
                    setInModifica({
                      id: d.id,
                      priceExw: String(Number(d.priceExw)),
                      costDdp: String(Number(d.costDdp)),
                      sconto: d.discountPercentage1 === null ? '' : String(Number(d.discountPercentage1)),
                    })
                  }
                >
                  {t('Modifica')}
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={togli.isPending}
                  onClick={() => togli.mutate({ lineId: riga.id, destId: d.id })}
                >
                  {t('Togli')}
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      {aperto && (
        <>
          {errore && (
            <div className="avviso" role="alert" style={{ margin: '10px 0' }}>
              <span className="avviso-segno">▲</span>
              <span>{errore}</span>
            </div>
          )}
          <div className="fields" style={{ marginTop: 10 }}>
            <div className="field span-2">
              <label htmlFor="d-ind">{t('Destinazione')}</label>
              <select
                id="d-ind"
                className="select"
                value={addressId}
                onChange={(e) => setAddressId(e.target.value)}
              >
                <option value="">{t('Scegli…')}</option>
                {disponibili.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                    {a.city ? ` \u2014 ${a.city}` : ''}
                  </option>
                ))}
              </select>
              {indirizzi.data && disponibili.length === 0 && (
                <span className="dim" style={{ fontSize: 11 }}>
                  {gia.length > 0
                    ? t('Tutte le destinazioni del cliente hanno già un prezzo su questa riga.')
                    : t('Questo listino non ha un cliente, quindi non ci sono destinazioni da scegliere.')}
                </span>
              )}
            </div>
            <div className="field">
              <label htmlFor="d-prezzo">{t('Prezzo EXW')}</label>
              <input
                id="d-prezzo"
                className="input num"
                type="number"
                step="0.001"
                min="0"
                value={priceExw}
                onChange={(e) => setPriceExw(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="d-iva">{t('Aliquota')}</label>
              <select
                id="d-iva"
                className="select"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
              >
                <option value="">{t('Quella della riga')}</option>
                {opzioni?.taxes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            <button className="btn" onClick={() => setAperto(false)}>
              {t('Annulla')}
            </button>
            <button
              className="btn btn-primary"
              onClick={conferma}
              disabled={aggiungi.isPending || addressId === '' || priceExw.trim() === ''}
            >
              {aggiungi.isPending ? 'Aggiungo…' : 'Aggiungi'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
